/**
 * What a round of a season is called.
 *
 * One module, because the word was written out in nine places and had drifted
 * into five different words. On a single night a member could see the fixtures
 * page say "Gameweek 3", the poster for the same night say "MATCH DAY 3", the
 * team-sheet poster beside it say "GAMEWEEK 3", the scoring screen say "Match
 * day 3", the admin badge say "GW 3" and the homepage count them as "Match
 * Weeks". Every one of those was somebody writing the obvious thing where they
 * were standing, and none of them was wrong on its own.
 *
 * The word itself is MATCH DAY, and the reasons are worth writing down because
 * the question will come back.
 *
 *   It is what the league already says. Both of the posters this league had
 *   drawn by hand last season are headed MATCH DAY. The posters are what the
 *   members actually see, so the app moves to the artwork's word rather than
 *   the artwork moving to the app's.
 *
 *   It is true about the thing. A round is played out in one afternoon, not
 *   over a week. "Gameweek" is a fantasy-football word for a scheduling
 *   window, and it was borrowed here while the season was still being planned
 *   — which is exactly what it was good for and exactly why it can go.
 *
 *   It sits in the sequence that follows it. "Match Day 11, then the
 *   Quarter-finals" is one vocabulary. "Gameweek 11, then the Quarter-finals"
 *   is two, and the second half is the half nobody would rename.
 *
 * If it should be something else, change WORD and nothing else. That is the
 * point of this file existing rather than a find-and-replace.
 */

/** The word, singular, in the case an English sentence would use. */
const WORD = 'Match Day';

/** "Match Day 3". The heading on a poster, a fixtures page, a night. */
export const roundName = (gameweek: number): string => `${WORD} ${gameweek}`;

/**
 * "MD 3", for the places a full name will not fit — an admin row's badge, a
 * chip beside a fixture.
 *
 * Derived from WORD rather than written out, so a rename cannot leave the
 * short form saying something the long form no longer does. That is precisely
 * how "GW" outlived the label it was short for.
 */
export const roundShort = (gameweek: number): string =>
  `${WORD.split(' ').map((w) => w[0]).join('')} ${gameweek}`;

/**
 * The plural, for counting them: "11 Match Days".
 *
 * Naive, and deliberately so — it pluralises the last word by adding an s,
 * which is right for every name this is ever going to hold. A pluralisation
 * library for one string is the wrong trade.
 */
export const roundsNoun = (): string => `${WORD}s`;

/**
 * What a knockout round is called, where a season has one.
 *
 * Here rather than in the endpoint that used to hold it, because these are the
 * same question as the one above — what do we call this round — and the whole
 * argument for "Match Day" is that it reads correctly in front of these. Two
 * files would let the two halves of one sequence drift apart again.
 *
 * A stage this does not know is returned as it was given rather than replaced
 * with something friendlier. A raw `third_place` on a page is a bug anybody can
 * see and report; a plausible-looking wrong name is one nobody will.
 */
const STAGE_LABEL: Record<string, string> = {
  quarter_final: 'Quarter-finals',
  semi_final: 'Semi-finals',
  third_place: 'Third-place playoff',
  // Premier Pearls: the two sides who missed the semi-finals.
  fifth_place: 'Fifth-place playoff',
  // The Page playoff, where the beaten top-two side drops into the
  // preliminary rather than out, and its winner plays for the title.
  qualifying_final: 'Qualifying final',
  elimination_final: 'Elimination final',
  preliminary_final: 'Preliminary final',
  final: 'Final',
};

export const stageName = (stage: string): string => STAGE_LABEL[stage] ?? stage;

/**
 * What to call a round, whichever kind it is.
 *
 * The one function most callers want: they have a fixture, it has a stage and
 * a gameweek, and they need a heading.
 */
export const roundLabel = (stage: string, gameweek: number): string =>
  stage === 'league' ? roundName(gameweek) : stageName(stage);

/**
 * The same, for a badge or a scoreboard with room for "MD 4" but not "Match
 * Day 4".
 *
 * WHY THIS EXISTS: three screens printed `roundShort(gameweek)` straight off
 * the fixture, and a knockout fixture's gameweek is not a match day. Norwood
 * numbers its knockout from 100 so it sorts after the league, so its Final
 * read "MD 104" on its own match page — a night that is called the Final
 * everywhere else on the site. The stage is the name; the gameweek is only
 * for ordering.
 *
 * A missing stage is treated as a league night, which is what every fixture
 * was before knockouts existed and what a cached page from then will send.
 */
export const roundShortLabel = (stage: string | null | undefined, gameweek: number): string =>
  !stage || stage === 'league' ? roundShort(gameweek) : stageName(stage);

/**
 * The few characters a round gets on a chip in a row of them — the Night
 * recap's picker. A match day keeps its number; a knockout round gets the
 * shortest name a player would recognise.
 */
const STAGE_CHIP: Record<string, string> = {
  quarter_final: 'QF',
  semi_final: 'SF',
  third_place: '3rd',
  fifth_place: '5th',
  qualifying_final: 'QualF',
  elimination_final: 'EF',
  preliminary_final: 'PF',
  final: 'Final',
};

export const roundChip = (stage: string | null | undefined, gameweek: number): string =>
  !stage || stage === 'league' ? String(gameweek) : (STAGE_CHIP[stage] ?? stageName(stage));

/**
 * Rounds by how much they decide, most first. A night can hold more than one —
 * Premier Pearls play their semi-finals and fifth-place playoff on the same
 * afternoon, in the same gameweek — and it is named for the round that matters
 * most. It was named alphabetically, and the semi-finals night read "5th".
 */
const STAGE_WEIGHT = [
  'final',
  'preliminary_final',
  'semi_final',
  'qualifying_final',
  'elimination_final',
  'quarter_final',
  'third_place',
  'fifth_place',
  'league',
];

const weight = (stage: string) => {
  const i = STAGE_WEIGHT.indexOf(stage);
  return i === -1 ? STAGE_WEIGHT.length : i;
};

export const orderStages = (stages: string[]): string[] =>
  [...new Set(stages)].sort((a, b) => weight(a) - weight(b));

export const mainStage = (stages: string[]): string => orderStages(stages)[0] ?? 'league';
